/**
 * BeanCounter — số đếm Bean tăng/giảm smooth với @react-spring/web.
 *
 * Props:
 *   - value: số Bean hiện tại (target).
 *   - size: 'small' | 'medium' | 'large'.
 *
 * Tham chiếu:
 *   - `docs/analysis/02_use_cases.md` UC-06 §7.2 (BeanCounter animate 0.6s).
 *   - design/3.Thiết kế màn hình chính ShopScreen.png (số HUD góc trên).
 */
import { useSpring, animated } from "@react-spring/web";

import { formatBean } from "../lib/format";

type Size = "small" | "medium" | "large";

const FONT_SIZE_CLASS: Record<Size, string> = {
  small: "text-base",
  medium: "text-2xl",
  large: "text-4xl",
};

interface Props {
  value: number;
  size?: Size;
  /** Override className test/styling. */
  className?: string;
  /** Data attribute để E2E test query. */
  testId?: string;
}

export function BeanCounter({
  value,
  size = "medium",
  className = "",
  testId = "bean-counter",
}: Props) {
  const { number } = useSpring({
    to: { number: value },
    from: { number: 0 },
    immediate: false,
    config: { mass: 1, tension: 280, friction: 60 },
    reset: false,
  });

  const fontClass = FONT_SIZE_CLASS[size];

  return (
    <div
      data-testid={testId}
      className={`flex items-center gap-1 font-bold text-coffee ${fontClass} ${className}`}
    >
      <span aria-hidden="true">☕</span>
      <animated.span aria-label="Bean">
        {number.to((n) => formatBean(n))}
      </animated.span>
    </div>
  );
}
